import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Container, Row, Col, Card, CardHeader, Button, Input, Label, CardBody, Form, FormFeedback } from "@/Components/ui";
import toast from "react-hot-toast";

import * as Yup from "yup";
import { useFormik } from "formik";

import { API_USERS_CRM, API_USERS_INQUIRY, changeUserDetail, changeUserPassword, getDataFromUrl } from "../../../api";
import BreadCrumb from "../../../Components/Common/BreadCrumb";

import avatar from "../../../assets/images/users/user-dummy-img.jpg";
import UserDetailReservationList from "./UserDetailReservationList";
import UserDetailTicketList from "./UserDetailTicketList";
import UserDetailVoucherList from "./UserDetailVoucherList";

import { SUBDOMAIN } from "../../../assets/meta/meta";
import UserDetailInquiryList from "./UserDetailInquiryList";
import UserDetailReservationCancelList from "./UserDetailReservationCancelList";
import UserDetailTicketCancelList from "./UserDetailTicketCancelList";
import UserDetailPrecouponList from "./UserDetailPrecouponList";
import UserDetailCouponList from "./UserDetailCouponList";
import UserSendModal from "../UserList/send-modal";
import UserOrderTotalList from "../../Orders/UserOrderTotalList";

const UserDetail = () => {
  const { id } = useParams();

  const [user, setUser] = useState(null);

  const [inquiry, setInquiry] = useState(null);
  const [showUserDetailsForm, setShowUserDetailsForm] = useState(false);

  // Toggle states for each section
  const [showReservations, setShowReservations] = useState(false);
  const [showReservationCancels, setShowReservationCancels] = useState(false);
  const [showTickets, setShowTickets] = useState(false);
  const [showTicketCancels, setShowTicketCancels] = useState(false);
  const [showVouchers, setShowVouchers] = useState(false);
  const [showPrecoupons, setShowPrecoupons] = useState(false);
  const [showCoupons, setShowCoupons] = useState(false);
  const [showInquiries, setShowInquiries] = useState(false);
  const [showOrders, setShowOrders] = useState(false);

  const [sendModal, setSendModal] = useState(false);

  const fetchUser = useCallback(() => {
    getDataFromUrl(`${API_USERS_CRM}/${id}/`)
      .then((data) => {
        console.log("user:", data);
        // id, 가입일, 이름, 전화번호
        setUser(data);
      })
      .catch((e) => {
        console.log("get user error:", e);
      });
  }, [id]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    if (user && user["전화번호"]) {
      // 두 번째 API 호출 (user 상태가 설정된 후)
      getDataFromUrl(`${API_USERS_INQUIRY}/?subdomain=${SUBDOMAIN}&phone_num=${user["전화번호"]}`)
        .then((data) => {
          console.log("inquiry:", data);
          setInquiry(data);
        })
        .catch((e) => {
          console.log("get inquiry error:", e);
        });
    }
  }, [user]);

  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: false,

    initialValues: {
      username: "",
      business_name: "",
      password: "",
      verifyPassword: "",
    },
    validationSchema: Yup.object({
      username: Yup.string().required("Please Enter Your UserName"),
      business_name: Yup.string().max(100, "Business name must be 100 characters or less").nullable(),
      password: Yup.string().min(4, "Password must be 4 characters long"),
      verifyPassword: Yup.string().oneOf([Yup.ref("password"), null], 'Must match "password" field value'),
    }),
    onSubmit: (values) => {
        //   update
      (async () => {
        console.log("form", values);

        try {
          if (values.username !== user["이름"]) {
            await changeUserDetail(user["id"], {
              phone_num: user["전화번호"],
              username: values.username,
              business_name: values.business_name || null,
            });
          }
          else if ((user["business_name"] || "") !== (values.business_name || "")) {
            await changeUserDetail(user["id"], {
              phone_num: user["전화번호"],
              username: values.username,
              business_name: values.business_name || null,
            });
          }
          if (values.password && values.password === values.verifyPassword) {
            await changeUserPassword(user["전화번호"], values.password);
          }
          toast.success("사용자 정보가 수정되었습니다.");
          await fetchUser();
          setShowUserDetailsForm(false);
        } catch (error) {
          console.warn(error);
          toast.error("사용자 정보 수정에 실패했습니다.");
        }
      })();
    },
  });

  useEffect(() => {
    if (user) {
      validation.setValues({
        username: user["이름"],
        business_name: user["business_name"] || "",
        password: "",
        verifyPassword: "",
      });
    }
  }, [user]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="사용자 상세" pageTitle="사용자" />
          <Row>
            <Col lg={12}>
              <Card id="customerList">
                <CardHeader className="border-0">
                  <Row className="g-4 align-items-center">
                    <div className="col-sm">
                      <div>
                        <h5 className="card-title mb-0">사용자 상세정보</h5>
                      </div>
                    </div>
                  </Row>
                  <Row>
                    <Col lg="12">

                      {user ? (
                        <CardBody>
                          <div className="d-flex">
                            <div className="mx-3">
                              <img src={avatar} alt="" className="avatar-md rounded-circle img-thumbnail" />
                            </div>
                            <div className="grow align-self-center">
                              <div className="text-muted">
                                <h5>{user["이름"]}</h5>
                                <p className="mb-0">ID : {user["id"]}</p>
                                <p className="mb-1">전화번호 : {user["전화번호"]}</p>
                                <p className="mb-0">제휴사명 : {user["business_name"] || "-"}</p>
                              </div>
                            </div>
                          </div>
                        </CardBody>
                      ) : null}
                    </Col>
                  </Row>
                  <Row>
                    <Col lg="12">
                      <div className="text-start">
                        <Button color="primary" onClick={() => setSendModal(true)}>
                          쿠폰/예약권/교환권 전송
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </CardHeader>
              </Card>

              <h4 className="card-title mb-4">
                사용자 정보 수정
                <span className="text-muted fs-13 ms-2">사용자 정보를 수정합니다.</span>
                <Button
                  color="link"
                  className="ms-2 p-0"
                  onClick={() => setShowUserDetailsForm(!showUserDetailsForm)}
                >
                  {showUserDetailsForm ? "숨기기" : "보이기"}
                </Button>
              </h4>

              {user && showUserDetailsForm ? (
                <Card>
                  <CardBody>
                    <Form
                      className="form-horizontal"
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                    >
                      <div className="form-group">
                        <Label className="form-label">회원 이름</Label>
                        <Input
                          name="username"
                          // value={name}
                          className="form-control"
                          placeholder="Enter User Name"
                          type="text"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.username || ""}
                          invalid={validation.touched.username && validation.errors.username ? true : false}
                        />
                        {validation.touched.username && validation.errors.username ? <FormFeedback type="invalid">{validation.errors.username}</FormFeedback> : null}
                        <Input name="idx" value={user["id"]} type="hidden" />

                        <div style={{ margin: "12px" }}></div>
                        <Label className="form-label">제휴사명</Label>
                        <Input
                          name="business_name"
                          className="form-control"
                          placeholder="Business Name"
                          type="text"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.business_name || ""}
                          invalid={validation.touched.business_name && validation.errors.business_name ? true : false}
                        />
                        {validation.touched.business_name && validation.errors.business_name ? <FormFeedback type="invalid">{validation.errors.business_name}</FormFeedback> : null}

                        <div style={{ margin: "12px" }}></div>
                        <Label className="form-label">비밀번호</Label>
                        <Input
                          name="password"
                          className="form-control"
                          placeholder="Change Password ..."
                          type="password"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.password}
                          invalid={validation.touched.password && validation.errors.password ? true : false}
                        />
                        {validation.touched.password && validation.errors.password ? <FormFeedback type="invalid">{validation.errors.password}</FormFeedback> : null}

                        <div style={{ margin: "8px" }}></div>
                        <Label className="form-label">비밀번호 확인</Label>
                        <Input
                          name="verifyPassword"
                          className="form-control"
                          placeholder="Verify Password ..."
                          type="password"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.verifyPassword}
                          invalid={validation.touched.verifyPassword && validation.errors.verifyPassword ? true : false}
                        />
                        {validation.touched.verifyPassword && validation.errors.verifyPassword ? <FormFeedback type="invalid">{validation.errors.verifyPassword}</FormFeedback> : null}
                      </div>
                      {/* <div className="form-group">
                                        </div> */}
                      <div className="text-center mt-4">
                        <Button type="submit" color="danger">
                          Update
                        </Button>
                      </div>
                    </Form>
                  </CardBody>
                </Card>
              ) : null}

              {user ? (
                <Card>
                  <CardHeader className="border-0">
                    <Row className="g-4 align-items-center">
                      <div className="col-sm">
                        <div>
                          <h4 className="card-title mb-0">
                            주문 내역
                            <Button
                              color="link"
                              className="ms-2 p-0"
                              onClick={() => setShowOrders(!showOrders)}
                            >
                              {showOrders ? "숨기기" : "보이기"}
                            </Button>
                          </h4>
                        </div>
                      </div>
                    </Row>
                  </CardHeader>
                  {showOrders && (
                    <CardBody>
                      <UserOrderTotalList fixedFilterPhone={user?.phone_num || user?.["전화번호"] || ""} />
                    </CardBody>
                  )}
                </Card>
              ) : null}

              {user ? (
                <Card>
                  <CardHeader className="border-0">
                    <Row className="g-4 align-items-center">
                      <div className="col-sm">
                        <div>
                          <h4 className="card-title mb-0">
                            예약
                            <Button
                              color="link"
                              className="ms-2 p-0"
                              onClick={() => setShowReservations(!showReservations)}
                            >
                              {showReservations ? "숨기기" : "보이기"}
                            </Button>
                          </h4>
                        </div>
                      </div>
                    </Row>
                  </CardHeader>
                  {showReservations && (
                    <CardBody>
                      <UserDetailReservationList userPhoneNum={user["전화번호"]} />
                    </CardBody>
                  )}
                </Card>
              ) : null}

              {user ? (
                <Card>
                  <CardHeader className="border-0">
                    <Row className="g-4 align-items-center">
                      <div className="col-sm">
                        <div>
                          <h4 className="card-title mb-0">
                            예약 취소
                            <Button
                              color="link"
                              className="ms-2 p-0"
                              onClick={() => setShowReservationCancels(!showReservationCancels)}
                            >
                              {showReservationCancels ? "숨기기" : "보이기"}
                            </Button>
                          </h4>
                        </div>
                      </div>
                    </Row>
                  </CardHeader>
                  {showReservationCancels && (
                    <CardBody>
                      <UserDetailReservationCancelList userPhoneNum={user["전화번호"]} />
                    </CardBody>
                  )}
                </Card>
              ) : null}

              {user ? (
                <Card>
                  <CardHeader className="border-0">
                    <Row className="g-4 align-items-center">
                      <div className="col-sm">
                        <div>
                          <h4 className="card-title mb-0">
                            티켓
                            <Button
                              color="link"
                              className="ms-2 p-0"
                              onClick={() => setShowTickets(!showTickets)}
                            >
                              {showTickets ? "숨기기" : "보이기"}
                            </Button>
                          </h4>
                        </div>
                      </div>
                    </Row>
                  </CardHeader>
                  {showTickets && (
                    <CardBody>
                      <UserDetailTicketList userPhoneNum={user["전화번호"]} />
                    </CardBody>
                  )}
                </Card>
              ) : null}

              {user ? (
                <Card>
                  <CardHeader className="border-0">
                    <Row className="g-4 align-items-center">
                      <div className="col-sm">
                        <div>
                          <h4 className="card-title mb-0">
                            티켓 취소
                            <Button
                              color="link"
                              className="ms-2 p-0"
                              onClick={() => setShowTicketCancels(!showTicketCancels)}
                            >
                              {showTicketCancels ? "숨기기" : "보이기"}
                            </Button>
                          </h4>
                        </div>
                      </div>
                    </Row>
                  </CardHeader>
                  {showTicketCancels && (
                    <CardBody>
                      <UserDetailTicketCancelList userPhoneNum={user["전화번호"]} />
                    </CardBody>
                  )}
                </Card>
              ) : null}

              {user ? (
                <Card>
                  <CardHeader className="border-0">
                    <Row className="g-4 align-items-center">
                      <div className="col-sm">
                        <div>
                          <h4 className="card-title mb-0">
                            교환권
                            <Button
                              color="link"
                              className="ms-2 p-0"
                              onClick={() => setShowVouchers(!showVouchers)}
                            >
                              {showVouchers ? "숨기기" : "보이기"}
                            </Button>
                          </h4>
                        </div>
                      </div>
                    </Row>
                  </CardHeader>
                  {showVouchers && (
                    <CardBody>
                      <UserDetailVoucherList userPhoneNum={user["전화번호"]} />
                    </CardBody>
                  )}
                </Card>
              ) : null}

              {user ? (
                <Card>
                  <CardHeader className="border-0">
                    <Row className="g-4 align-items-center">
                      <div className="col-sm">
                        <div>
                          <h4 className="card-title mb-0">
                            예약권
                            <Button
                              color="link"
                              className="ms-2 p-0"
                              onClick={() => setShowPrecoupons(!showPrecoupons)}
                            >
                              {showPrecoupons ? "숨기기" : "보이기"}
                            </Button>
                          </h4>
                        </div>
                      </div>
                    </Row>
                  </CardHeader>
                  {showPrecoupons && (
                    <CardBody>
                      <UserDetailPrecouponList userPhoneNum={user["전화번호"]} />
                    </CardBody>
                  )}
                </Card>
              ) : null}

              {user ? (
                <Card>
                  <CardHeader className="border-0">
                    <Row className="g-4 align-items-center">
                      <div className="col-sm">
                        <div>
                          <h4 className="card-title mb-0">
                            쿠폰(할인)
                            <Button
                              color="link"
                              className="ms-2 p-0"
                              onClick={() => setShowCoupons(!showCoupons)}
                            >
                              {showCoupons ? "숨기기" : "보이기"}
                            </Button>
                          </h4>
                        </div>
                      </div>
                    </Row>
                  </CardHeader>
                  {showCoupons && (
                    <CardBody>
                      <UserDetailCouponList userPhoneNum={user["전화번호"]} />
                    </CardBody>
                  )}
                </Card>
              ) : null}

              {user ? (
                <Card>
                  <CardHeader className="border-0">
                    <Row className="g-4 align-items-center">
                      <div className="col-sm">
                        <div>
                          <h4 className="card-title mb-0">
                            문의 게시판
                            <Button
                              color="link"
                              className="ms-2 p-0"
                              onClick={() => setShowInquiries(!showInquiries)}
                            >
                              {showInquiries ? "숨기기" : "보이기"}
                            </Button>
                          </h4>
                        </div>
                      </div>
                    </Row>
                  </CardHeader>
                  {showInquiries && (
                    <CardBody>
                      <UserDetailInquiryList useInquiry={inquiry} />
                    </CardBody>
                  )}
                </Card>
              ) : null}
            </Col>
          </Row>
        </Container>
      </div>

      <UserSendModal
        visible={sendModal}
        setVisible={setSendModal}
        userList={user ? [user["전화번호"]] : []}

        onSendSuccess={(message) => {
          toast.success(message);
          setSendModal(false);
        }}

        onSendError={(message) => {
          toast.error(message);
          setSendModal(false);
        }}
      />
    </React.Fragment>
  );
};

export default UserDetail;
